// Remplacement navigateur du SDK Firebase pour l'admin (migration
// Firestore -> MySQL) : imite le sous-ensemble de l'API effectivement
// utilisé par les pages (firestore + auth) et délègue tout à l'API Hermes
// via la passerelle générique /api/db (api/controllers/db.js).
//
// Les composants ne changent que leur import :
//   import firebase from '@/utils/firestore-shim'
// Le reste du code fonctionne tel quel : collection("Sites").doc(host)
// .set(...), collection("Sites").doc(host).collection("Products").get(),
// where(...).get() puis snapshot.forEach / doc.data / doc.ref,
// fs.batch().delete(ref).commit(), firebase.firestore.Timestamp.now(),
// firebase.auth().signInWithEmailAndPassword / signOut / currentUser...
//
// Session : le jeton JWT de POST /api/auth/login vit dans localStorage
// (« admin_token », expiration « tokenExpiry ») et part dans l'en-tête
// Authorization: Bearer de chaque appel /api/db/*. Une session expirée
// (401 code « admin_session ») ramène à la page de connexion.
import config from '../../config.json';

const apiUrl = String(config.apiUrl || '').replace(/\/+$/, '');
const TOKEN_KEY = 'admin_token';
const TOKEN_EXPIRY_KEY = 'tokenExpiry';

function apiRequest(method, path, body) {
    const headers = { 'Content-Type': 'application/json', 'x-api-key': config.apiKey || '' };
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) headers['Authorization'] = 'Bearer ' + token;
    const options = { method: method, headers: headers };
    if (body !== undefined && body !== null) options.body = JSON.stringify(body);
    return fetch(apiUrl + '/api' + path, options).then(function (res) {
        return res.json().catch(function () { return null; }).then(function (data) {
            if (data === null) {
                // Réponse non JSON (page HTML d'un serveur mal configuré...) :
                // on échoue bruyamment plutôt que d'avaler une réponse vide.
                throw new Error('Réponse inattendue du serveur (HTTP ' + res.status + ', pas de JSON).');
            }
            if (res.status === 401 && data && data.code === 'admin_session') {
                // Session expirée : on nettoie et on repart vers la
                // connexion (pas de redirection si on y est déjà).
                localStorage.removeItem(TOKEN_KEY);
                if (window.location.pathname !== '/auth/login') {
                    window.location.assign('/auth/login');
                }
                throw new Error(data.error || 'Session admin expirée.');
            }
            if (!res.ok) throw new Error((data && data.error) || ('Erreur ' + res.status));
            return data;
        });
    });
}

function apiPost(path, body) {
    return apiRequest('POST', path, body || {});
}

function apiGet(path) {
    return apiRequest('GET', path);
}

function apiPatch(path, body) {
    return apiRequest('PATCH', path, body || {});
}

// ---------------------------------------------------------------------------
// Snapshots (mêmes formes que ceux du SDK Firestore).
// ---------------------------------------------------------------------------

class DocSnapshot {
    constructor(id, data, ref) {
        this.id = id;
        this.exists = data !== null;
        this.ref = ref;
        this._data = data;
    }

    data() {
        return this._data === null ? undefined : this._data;
    }
}

class QuerySnapshot {
    constructor(docs) {
        this.docs = docs;
        this.empty = docs.length === 0;
        this.size = docs.length;
    }

    forEach(fn) {
        this.docs.forEach(fn);
    }
}

// ---------------------------------------------------------------------------
// Références : les segments alternent collection et document, comme un
// chemin Firestore (« Sites » + « host » + « Products » + « id »).
// ---------------------------------------------------------------------------

class DocRef {
    constructor(segments) {
        this._segments = segments;
    }

    get id() {
        return this._segments[this._segments.length - 1];
    }

    get() {
        return apiPost('/db/get', { path: this._segments }).then((res) => {
            return new DocSnapshot(res.id, res.exists ? res.data : null, this);
        });
    }

    set(data) {
        return apiPost('/db/set', { path: this._segments, data: data })
            .then(() => ({ writeTime: new Date() }));
    }

    update(data) {
        return apiPost('/db/update', { path: this._segments, data: data })
            .then(() => ({ writeTime: new Date() }));
    }

    delete() {
        return apiPost('/db/delete', { path: this._segments })
            .then(() => ({ writeTime: new Date() }));
    }

    collection(name) {
        return new CollectionRef(this._segments.concat(String(name)));
    }
}

class Query {
    constructor(segments, ops) {
        this._segments = segments;
        this._ops = ops || [];
    }

    _with(op) {
        return new Query(this._segments, this._ops.concat([op]));
    }

    where(field, op, value) {
        return this._with({ kind: 'where', field: field, op: op, value: value });
    }

    orderBy(field, direction) {
        return this._with({ kind: 'orderBy', field: field, direction: direction || 'asc' });
    }

    limit(count) {
        return this._with({ kind: 'limit', count: count });
    }

    get() {
        const wheres = [];
        const orderBy = [];
        let limit;
        for (const op of this._ops) {
            if (op.kind === 'where') wheres.push([op.field, op.op, op.value]);
            else if (op.kind === 'orderBy') orderBy.push([op.field, op.direction]);
            else if (op.kind === 'limit') limit = op.count;
        }
        const body = { path: this._segments, wheres: wheres, orderBy: orderBy };
        if (limit !== undefined) body.limit = limit;
        return apiPost('/db/get', body).then((res) => {
            const docs = (res.docs || []).map((doc) => {
                return new DocSnapshot(doc.id, doc.data, new DocRef(this._segments.concat(doc.id)));
            });
            return new QuerySnapshot(docs);
        });
    }
}

class CollectionRef extends Query {
    doc(id) {
        return new DocRef(this._segments.concat(String(id)));
    }

    add(data) {
        return apiPost('/db/add', { path: this._segments, data: data }).then((res) => {
            return new DocRef(this._segments.concat(res.id));
        });
    }
}

class WriteBatch {
    constructor() {
        this._ops = [];
    }

    set(ref, data) {
        this._ops.push({ op: 'set', path: ref._segments, data: data });
        return this;
    }

    update(ref, data) {
        this._ops.push({ op: 'update', path: ref._segments, data: data });
        return this;
    }

    delete(ref) {
        this._ops.push({ op: 'delete', path: ref._segments });
        return this;
    }

    commit() {
        return apiPost('/db/batch', { ops: this._ops })
            .then(() => ({ writeTime: new Date() }));
    }
}

class FirestoreShim {
    collection(name) {
        // Le SDK acceptait aussi les chemins complets à barres obliques
        // (« Sites/host/Products », utilisé par la suppression de boutique).
        const segments = String(name || '').split('/').filter((segment) => segment !== '');
        if (!segments.length) throw new Error('collection() : nom de collection requis.');
        return new CollectionRef(segments);
    }

    batch() {
        return new WriteBatch();
    }
}

function firestore() {
    return new FirestoreShim();
}

// Même convention que la façade MySQL côté API : les dates nouvelles
// (created des articles...) sont des chaînes ISO lisibles par les pages.
firestore.Timestamp = {
    now: function () { return new Date().toISOString(); }
};

// ---------------------------------------------------------------------------
// Authentification : comptes SaaS (e-mail + mot de passe, POST
// /api/auth/register et /api/auth/login) avec repli super-admin via
// ADMIN_PASSWORD. Le jeton JWT sert ensuite aux appels /api/db/* et
// /api/auth/* et /api/billing/*.
// ---------------------------------------------------------------------------

const USERINFO_KEY = 'userinfo';

// Enregistre la session (jeton + expiration) et le profil affiché.
function persistSession(data, fallbackEmail) {
    const token = data.token || '';
    const expiresIn = Number(data.expiresIn) || 12 * 60 * 60;
    const user = data.user || {};
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_EXPIRY_KEY, String(Date.now() + expiresIn * 1000));
    localStorage.setItem(USERINFO_KEY, JSON.stringify({
        id: user.id || null,
        email: user.email || fallbackEmail || '',
        displayName: user.displayName || (fallbackEmail || 'Admin'),
        photoURL: user.photoURL || null,
        role: user.role || 'owner'
    }));
    return {
        user: {
            refreshToken: token,
            uid: user.id || null,
            displayName: user.displayName || (fallbackEmail || 'Admin'),
            email: user.email || fallbackEmail || '',
            photoURL: user.photoURL || null,
            role: user.role || 'owner'
        }
    };
}

const authApi = {
    // Connexion par e-mail + mot de passe (le serveur accepte aussi le
    // super-admin : mot de passe = ADMIN_PASSWORD, quel que soit l'e-mail).
    signInWithEmailAndPassword(email, password) {
        return apiPost('/auth/login', { email: email, password: password })
            .then((data) => persistSession(data, email));
    },

    // Création de compte (onglet Register) : renvoie directement une
    // session valide (auto-login), comme le SDK Firebase.
    createUserWithEmailAndPassword(email, password) {
        return apiPost('/auth/register', { email: email, password: password })
            .then((data) => persistSession(data, email));
    },

    // Profil complet (boutiques, abonnement, droits) : GET /api/auth/me.
    // Met à jour « userinfo » et renvoie { user, sites, subscription, entitlement }.
    fetchProfile() {
        return apiGet('/auth/me').then((data) => {
            const user = data.user || {};
            localStorage.setItem(USERINFO_KEY, JSON.stringify({
                id: user.id || null,
                email: user.email || '',
                displayName: user.displayName || 'Admin',
                photoURL: user.photoURL || null,
                role: user.role || 'owner'
            }));
            return data;
        });
    },

    signOut() {
        localStorage.removeItem(TOKEN_KEY);
        return Promise.resolve();
    },

    // Le SDK persistait l'utilisateur ; ici on le reconstruit depuis les
    // clés localStorage posées à la connexion (shim + auth/index.js).
    get currentUser() {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) return null;
        let info = {};
        try { info = JSON.parse(localStorage.getItem(USERINFO_KEY)) || {}; } catch (e) { /* infos optionnelles */ }
        return {
            uid: info.id || null,
            email: info.email || 'admin',
            displayName: info.displayName || 'Admin',
            photoURL: info.photoURL || null,
            role: info.role || 'owner',
            refreshToken: token
        };
    },

    // Compat : le SDK appelait le callback à chaque changement d'état ; le
    // routeur ne l'utilise plus, on répond une fois avec l'état actuel.
    onAuthStateChanged(callback) {
        setTimeout(function () { callback(authApi.currentUser); }, 0);
        return function () { /* désabonnement sans effet */ };
    }
};

// Export compatible « import firebase from '@/utils/firestore-shim' » :
// initializeApp() devient sans effet (aucune configuration locale).
// « api » expose les appels REST authentifiés (profil, boutiques,
// facturation) pour les pages de compte/abonnement.
export default {
    initializeApp: function () { return {}; },
    firestore: firestore,
    auth: function () { return authApi; },
    api: {
        get: apiGet,
        post: apiPost,
        patch: apiPatch,
        request: apiRequest
    }
};
